import { Link } from "react-router-dom";
import {
  ArrowRight, GraduationCap, BookOpen, Eye, Ear, Sparkles, Users, Compass,
  ShieldCheck, MessageCircle,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";

// ---------------------------------------------------------------------------
// PUBLIC PREVIEW PAGE — /academy/patients-voice-in-care
// This is a PUBLIC information/preview page for PVC-001. It does NOT expose the
// internal learner course, bypass Academy authentication, or create any
// enrollment. PVC-001 remains IN DEVELOPMENT / NOT YET ENROLLING.
// ---------------------------------------------------------------------------

const JOURNEY = [
  { code: "SEE",   label: "See the Person",         icon: Eye,
    body: "Recognize the person, story, identity, relationships, concerns, and hopes beyond the diagnosis." },
  { code: "HEAR",  label: "Hear the Voice",         icon: Ear,
    body: "Create space for concerns, wishes, grief, and conversations that may remain unspoken." },
  { code: "HONOR", label: "Honor What Matters",     icon: Sparkles,
    body: "Explore purpose, memory, legacy, relationships, children, reconciliation, and what remains meaningful to the patient." },
  { code: "STAY",  label: "Stay Present",           icon: Users,
    body: "Preserve dignity and compassionate presence as illness progresses and communication changes." },
  { code: "CARRY", label: "Carry the Voice Forward", icon: Compass,
    body: "Consider caregiver transition, grief, what families carry forward, and what each patient teaches the professionals who cared for them." },
];

const AUDIENCE = [
  "Nurses",
  "Social workers",
  "Hospice aides",
  "Physicians / providers",
  "Chaplains",
  "Care coordinators",
  "Volunteers",
  "Bereavement staff",
  "Healthcare leaders",
  "Other professionals supporting people through serious illness and end-of-life care",
];

const FORMAT = [
  "5 Sessions",
  "Online",
  "Self-Paced",
  "Interdisciplinary",
  "Certificate of Completion planned when production enrollment opens",
];

export default function AcademyPatientsVoiceInCare() {
  usePageMeta({
    title: "The Patient's Voice in Care · PVC-001 | CCCHAPS Academy",
    description:
      "A five-session interdisciplinary course based on From Diagnosis to Goodbye. In development · Not yet enrolling.",
  });

  return (
    <div className="bg-[#FDFCF6]" data-testid="pvc-public-page">
      {/* HERO */}
      <section className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-3">
            CCCHAPS Academy
          </p>
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="pvc-public-status">
            <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800">
              In Development
            </span>
            <span className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600">
              Five-Session Interdisciplinary Course
            </span>
          </div>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]"
            data-testid="pvc-public-title"
          >
            The Patient&rsquo;s Voice in Care
          </h1>
          <p
            className="mt-3 font-heading text-2xl sm:text-3xl text-slate-700 leading-tight"
            data-testid="pvc-public-subtitle"
          >
            Bringing the Patient&rsquo;s Voice Into Hospice and Healthcare Practice
          </p>
          <p className="mt-6 text-slate-700 leading-relaxed max-w-3xl">
            Based on <em>From Diagnosis to Goodbye: Restoring the Patient&rsquo;s Voice Through Every Stage of the Journey</em> by Chaplain Derrick Hall.
          </p>

          <div className="mt-8 grid md:grid-cols-2 gap-6 max-w-4xl">
            <p className="text-slate-700 leading-relaxed">
              The Patient&rsquo;s Voice in Care helps hospice and healthcare professionals recognize what may be happening behind the diagnosis, behavior, and words so the person receiving care does not disappear beneath clinical tasks, family concerns, or illness progression.
            </p>
            <p className="text-slate-700 leading-relaxed">
              This is interdisciplinary education designed to help each professional strengthen patient-centered care within their own role and scope.
            </p>
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
          The Five-Session Journey
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
          SEE → HEAR → HONOR → STAY → CARRY
        </h2>
        <ol className="mt-10 space-y-4" data-testid="pvc-public-journey">
          {JOURNEY.map((step, i) => {
            const Icon = step.icon;
            return (
              <li
                key={step.code}
                data-testid={`pvc-public-step-${i + 1}`}
                className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 grid md:grid-cols-[auto_auto_1fr] gap-5 md:gap-7 items-start"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#4A6FA5]/10 text-[#4A6FA5] shrink-0">
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">
                    Session {i + 1}
                  </p>
                  <h3 className="mt-1 font-heading text-xl text-slate-900 leading-tight">
                    {step.code === "SEE"
                      ? "SEE THE PERSON"
                      : step.code === "HEAR"
                      ? "HEAR THE VOICE"
                      : step.code === "HONOR"
                      ? "HONOR WHAT MATTERS"
                      : step.code === "STAY"
                      ? "STAY PRESENT"
                      : "CARRY THE VOICE FORWARD"}
                  </h3>
                </div>
                <p className="text-slate-700 leading-relaxed">{step.body}</p>
              </li>
            );
          })}
        </ol>
      </section>

      {/* AUDIENCE */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
                Designed for
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
                Who this course is for
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                The Patient&rsquo;s Voice in Care is written for the whole interdisciplinary team.
              </p>
            </div>
            <ul className="grid sm:grid-cols-2 gap-2" data-testid="pvc-public-audience">
              {AUDIENCE.map((a) => (
                <li
                  key={a}
                  className="flex items-start gap-2 rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-slate-800"
                >
                  <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[#4A6FA5] shrink-0" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT THIS IS — AND IS NOT */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
              Scope
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
              What this course is — and is not.
            </h2>
          </div>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              The Patient&rsquo;s Voice in Care is an interdisciplinary, patient-centered education course.
            </p>
            <p>
              It is <em>not</em> chaplain training, spiritual assessment training, chaplain documentation training, ministry-intervention training, clinical signs-of-dying competency training, or medical decision-making training.
            </p>
            <p className="text-slate-800 font-medium">
              Each discipline should apply patient-centered principles within its own professional role and scope.
            </p>
          </div>
        </div>

        {/* Featured quote */}
        <blockquote
          data-testid="pvc-public-quote"
          className="mt-10 rounded-2xl border border-slate-200 bg-white p-8 md:p-10 border-l-4 border-l-[#4A6FA5]"
        >
          <MessageCircle className="w-5 h-5 text-[#4A6FA5]" strokeWidth={1.75} />
          <p className="mt-3 font-heading text-2xl sm:text-3xl text-slate-800 leading-tight italic">
            &ldquo;A nurse should leave this course a better nurse. A social worker should leave a better social worker. An aide should leave a better aide. A chaplain should leave a better chaplain.&rdquo;
          </p>
        </blockquote>
      </section>

      {/* BOOK CROSS-LINK */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-20">
        <div
          className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#F0F4F8] via-white to-white p-8 md:p-10 grid md:grid-cols-[auto_1fr_auto] items-center gap-6 md:gap-8"
          data-testid="pvc-public-book-callout"
        >
          <div
            aria-hidden="true"
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white border border-slate-200 text-[#4A6FA5] shadow-sm shrink-0"
          >
            <BookOpen className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">
              Primary text
            </p>
            <h3 className="mt-1 font-heading text-2xl text-slate-900 leading-tight">
              From Diagnosis to Goodbye
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Restoring the Patient&rsquo;s Voice Through Every Stage of the Journey · by Chaplain Derrick Hall
            </p>
          </div>
          <Link
            to="/books/from-diagnosis-to-goodbye"
            data-testid="pvc-public-book-cta"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] whitespace-nowrap"
          >
            Read the Foundational Book <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FORMAT + DISCLAIMER */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
                Course format
              </p>
              <ul className="space-y-2" data-testid="pvc-public-format">
                {FORMAT.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2.5 rounded-lg bg-white border border-slate-200 px-4 py-3 text-sm text-slate-800"
                  >
                    <ShieldCheck className="w-4 h-4 text-[#4A6FA5] shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="pvc-public-disclaimer">
              <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
                Educational disclaimer
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                The Patient&rsquo;s Voice in Care is provided for educational and professional-development purposes. It does not replace professional medical advice, diagnosis, treatment, legal counsel, mental health services, organizational policies, or individualized recommendations from a patient&rsquo;s healthcare team.
              </p>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                Participants should practice within their professional role, scope, organizational policies, and applicable standards of care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CURRENT STATUS + CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F0F4F8] text-[#4A6FA5] mb-5">
          <GraduationCap className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
          Current status
        </p>
        <div className="flex items-center justify-center gap-2 flex-wrap mb-5">
          <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800">
            In Development
          </span>
          <span className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600">
            Not Yet Enrolling
          </span>
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
          The course has been developed and is currently being prepared for public enrollment.
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed max-w-2xl mx-auto">
          Organizations interested in future team training may contact CCCHAPS to begin a conversation.
        </p>
        <Link
          to="/contact"
          data-testid="pvc-public-cta-contact"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"
        >
          Ask About Future Team Training <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      {/* Track Three distinction footer */}
      <section className="bg-[#F8FAFC] border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 text-sm text-slate-700 leading-relaxed" data-testid="pvc-public-track-three-note">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-2">
              Looking for chaplain-specific training?
            </p>
            <p>
              The Patient&rsquo;s Voice in Care is patient-centered professional education for the broader healthcare and hospice team. It is distinct from Track Three Hospice Chaplaincy, which is the deeper specialty pathway for chaplains and spiritual-care providers.
            </p>
            <Link
              to="/academy/hospice-chaplaincy"
              data-testid="pvc-public-track-three-link"
              className="mt-4 inline-flex items-center gap-1.5 text-[#4A6FA5] font-medium hover:underline"
            >
              Explore Track Three Hospice Chaplaincy <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
